import * as React from "react";
import Api from "./Api";
import "./CustomerPage.scss";
import CheckCtl from "./input/CheckCtl";
import LookupCtl from "./input/LookupCtl";
import DateCtl from "./input/DateCtl";
import LabelCtl from "./input/LabelCtl";
import ModalCtl from "./input/ModalCtl";
import SelectCtl from "./input/SelectCtl";
import TextCtl from "./input/TextCtl";
import FileCtl from "./input/FileCtl";
import OwnerCtl, { OwnerRoles } from "./input/OwnerCtl";
import CustomerAddressCtl from "./CustomerAddressCtl"
import CustomerAddressDlg from "./CustomerAddressDlg"
import { isAccountingPersonnel } from "./Accounting";
import { Company } from "@testout/testout-commerce/models/hubspot/Company";
import { ContactName } from "@testout/testout-commerce/models/ContactName";
import { Document } from "@testout/testout-commerce/models/commerce/Document";
import { Customer, BillingType } from "@testout/testout-commerce/models/commerce/Customer";
import { CustomerAddress } from "@testout/testout-commerce/models/commerce/CustomerAddress";
import { TaxCertificate } from "@testout/testout-commerce/models/commerce/TaxCertificate";
import { PaymentTerms } from "@testout/testout-commerce/models/commerce/Payment";
import { ListItem } from "@testout/testout-commerce/models/ListItem";
import { PriceGroup } from "@testout/testout-commerce/models/commerce/PriceGroup";
import { PriceGroupCode } from "@testout/testout-commerce/models/commerce/PriceGroupCode";
import { PriceList } from "@testout/testout-commerce/models/commerce/PriceList";
import Segment from "@testout/testout-commerce/models/Segment";
import { Territory } from "@testout/testout-commerce/models/commerce/Territory";
import Dialog from "./input/Dialog";


interface CustomerPageProps {
    companyId?: string;
    customerNumber?: string;
}

interface CustomerPageState {
    initialized: boolean;
    inProgress: string;
    errorMessage: string;
    customer: Customer;
    company?: Company;
    territory?: Territory;
    taxCertificate: TaxCertificate;
    taxIsDirty: boolean;
    saveUniquePriceId: boolean;
    employees: ListItem[];

    priceCodes: ListItem[];
    priceCode: string;

    showAddressDlg: boolean;

    showAssociateDlg: boolean;
    associateCustomerNumber?: string;

    showDeleteDlg: boolean;
    deleteDlgHeader?: string;
    deleteDlgDescription?: string;
    deleteDlgButtonText?: string;

    pendingInheritPricing?: boolean;
    pendingPriceCode?: string;
    pendingParentCustomerNumber?: string;
    pendingSegment?: string;
}

export default class CustomerPage extends React.PureComponent<CustomerPageProps, CustomerPageState> {
    constructor(props: CustomerPageProps) {
        super(props);
        this.state = {
            showAddressDlg: false,
            initialized: false, inProgress: "", errorMessage: "",
            employees: [],
            customer: new Customer(), showDeleteDlg: false,
            priceCodes: [],
            priceCode: "",
            taxCertificate: new TaxCertificate(),
            taxIsDirty: false,
            saveUniquePriceId: false,
            showAssociateDlg: false,
        };
    }

    private _billingTypes: ListItem[] = [
        { name: BillingType[BillingType.None], value: BillingType.None.toString() },
        { name: BillingType[BillingType.LicenseActivation], value: BillingType.LicenseActivation.toString() },
        { name: BillingType[BillingType.ActivationCode], value: BillingType.ActivationCode.toString() }
    ];
    private _paymentTerms: ListItem[] = [
        { name: PaymentTerms.Cash.toString() },
        { name: PaymentTerms.Net15.toString() },
        { name: PaymentTerms.Net30.toString() },
        { name: PaymentTerms.Disc2in10Net30.toString() },
        { name: PaymentTerms.Net60.toString() },
        { name: PaymentTerms.Disc2in10Net60.toString() }
    ];

    async componentDidMount() {
        // Parse the URL:
        // customer?companyId=xxxx  - Associate a new or existing customer with HS company ID xxxx
        // customer/xxxx            - Bring up customer xxxx from the Commerce database
        // customer                 - Bring up a blank customer page

        // Save the employee list for lookups
        let employees = await Api.get<ListItem[]>('/api/employees/list-items');
        this.setState({ employees: employees });

        if (this.props.companyId) {
            let company = await Api.get<Company>(`/api/companies/${this.props.companyId}`);
            this.setState({ initialized: true, showAssociateDlg: true, company: company, associateCustomerNumber: company?.d365_account_number });
        }
        else if (this.props.customerNumber)
            this.loadCustomer();
        else
            this.initCustomer();
    }

    // Initialize the customer fields from company or default values
    initCustomer = async () => {
        try {
            this.setState({ errorMessage: "", inProgress: "Initializing from company..." });

            let company = this.state.company;
            let parentCompany = company?.parent_companyid ? await Api.get<Company>(`/api/companies/${company.parent_companyid}`) : undefined;
            let contact = company?.contacts.find(c => c.hs_object_id === company?.primary_contactid);
            let segment = new Segment(company?.segment ?? "EDU-2YR");

            let customer = new Customer({
                name: company?.name,
                segment: segment.code,
                paymentTerms: PaymentTerms.Net30.toString(),
                defaultTaxSchedule: "AVATAX",
                priceGroupId: segment.defaultPriceGroupId,
                priceId: segment.defaultPriceId,
                billingType: BillingType.None,
                creditOnHold: false,
                parentCustomerNumber: parentCompany?.d365_account_number,
                hideInCart: false,
                useAutomatedFulfillment: true,
                address: {
                    addressCode: "PRIMARY",
                    companyName: company?.name,
                    contactPerson: new ContactName({
                        firstNames: contact?.firstname,
                        lastName: contact?.lastname
                    }),
                    email: contact?.email,
                    phone: company?.phone,
                    postal: {
                        line1: company?.address,
                        line2: company?.address2,
                        city: company?.city,
                        stateOrProvince: company?.state,
                        postalCode: company?.zip,
                        countryCode: company?.country ?? "USA"
                    }
                }
            });
            let territory: Territory | undefined = customer?.territoryCode
                ? await Api.getObject(Territory, `/api/territories/${customer.territoryCode}`)
                : await Api.getObject(Territory, `/api/customers/territories?name=${encodeURIComponent(customer.name)}&segment=${customer.segment}&province=${customer.address.postal.stateOrProvince}&country=${customer.address.postal.countryCode}`);
            if (territory?.territoryCode) {
                customer.territoryCode = territory.territoryCode;
                customer.salespersonCode = territory.roles[OwnerRoles.AE] ?? territory.roles[OwnerRoles.CSM] ?? territory.roles[OwnerRoles.MCS];
            }
            else
                territory = undefined;

            let list = await Api.get<ListItem[]>(`/api/prices/grouppricecodes/${customer.priceGroupId}/list-items`);
            let pgc = await Api.get<PriceGroupCode>(`/api/prices/pricegroupcodebyid?priceGroupId=${customer.priceGroupId}&priceId=${customer.priceId}&includePrices=false`);

            this.setState({ initialized: true, showAssociateDlg: false, customer: customer, territory: territory, priceCodes: list, priceCode: pgc.priceCode, inProgress: "" });
        }
        catch (error: any) {
            this.setState({ errorMessage: `Error while loading the HubSpot company information: ${error.message}`, inProgress: "" });
        }
    }

    // Load an existing customer from the Commerce Server
    loadCustomer = async () => {
        try {
            let customer = await Api.getObject(Customer, `/api/customers/${this.props.customerNumber}`);
            let taxCertificate = await Api.getObject(TaxCertificate, `/api/customers/${this.props.customerNumber}/exemptions/current`);
            let territory: Territory|undefined = customer?.territoryCode
                ? await Api.getObject(Territory, `/api/territories/${customer.territoryCode}`)
                : await Api.getObject(Territory, `/api/customers/territories?name=${encodeURIComponent(customer.name)}&segment=${customer.segment}&province=${customer.address.postal.stateOrProvince}&country=${customer.address.postal.countryCode}`);
            if (!territory?.territoryCode)
                territory = undefined;
            
            let list = await Api.get<ListItem[]>(`/api/prices/grouppricecodes/${customer.priceGroupId}/list-items`);
            let pgc = await Api.get<PriceGroupCode>(`/api/prices/pricegroupcodebyid?priceGroupId=${customer.priceGroupId}&priceId=${customer.priceId}&includePrices=false`);

            this.setState({ initialized: true, customer: customer, territory: territory, taxCertificate: taxCertificate, priceCodes: list, priceCode: pgc.priceCode });
        }
        catch (error: any) {
            this.setState({ errorMessage: `Error while loading the customer: ${error.message}`, initialized: true });
        }
    }

    // Save the current customer to the Commerce Server
    saveCustomer = async () => {
        try {
            let customer = this.state.customer;

            // Validate that necessary fields have values
            let msg = "";
            if (!customer.name)
                msg += " Please enter a customer name.";
            if (!customer.territoryCode)
                msg += " Please enter a customer segment, state/province, or country so a territory can be determined.";
            else if (!customer.salespersonCode)
                msg += " Please select an owner from the territory roles above.";
            if (this.state.taxIsDirty) {
                if (!customer.address.postal.line1)
                    msg += " A street address is required for the tax exemption.";
                if (!customer.address.postal.city)
                    msg += " A city is required for the tax exemption.";
                if (!customer.address.postal.stateOrProvince)
                    msg += " A U.S. state is required for the tax exemption.";
                if (!customer.address.postal.postalCode)
                    msg += " A zip code is required for the tax exemption.";
                if (!this.state.taxCertificate?.document?.name)
                    msg += " An exemption file is required for the tax exemption."
            }
            if (msg) {
                this.setState({ errorMessage: msg });
                return;
            }

            this.setState({ errorMessage: "", inProgress: `Saving${this.props.companyId ? " and associating" : ""} customer...` });

            if (!customer.customerNumber) {
                customer.customerNumber = await Api.get<string>(`/api/customers-id?name=${encodeURIComponent(customer.name)}`);
                if (this.props.companyId)
                    await Api.patch<object>(`/api/companies/${this.props.companyId}`, { "d365_account_number": customer.customerNumber });
            }

            await Api.put<string>("/api/customers", customer);
            if (this.state.taxIsDirty) {
                await Api.put<void>(`/api/customers/${customer.customerNumber}/exemptions`, this.state.taxCertificate);
            }
            if (this.state.saveUniquePriceId && customer.uniquePriceGroupId === customer.priceGroupId) {
                let pg = await Api.get<PriceGroup>(`/api/prices/pricegroup?priceGroupId=${customer.priceGroupId}`);
                if (!pg)
                    throw new Error(`Unable to find unique price group ${customer.priceGroupId}`);
                let pl = await Api.get<PriceList>(`/api/prices/pricelist/${customer.priceId}?includePrices=false`);
                if (!pl)
                    throw new Error(`Unable to find price list ${customer.priceId}`);
                let pgc = await Api.get<PriceGroupCode>(`/api/prices/pricegroupcode?priceCode=${this.state.priceCode}`);
                if (!pgc) {
                    pgc = new PriceGroupCode();
                    pgc.priceGroupId = customer.priceGroupId;
                    pgc.priceId = customer.priceId;
                    pgc.version = "A";
                    pgc.priceCode = undefined;  // Force the price code to regenerate
                    await Api.put<void>('/api/prices/pricegroupcode', pgc);
                }
            }

            this.setState({ inProgress: "", taxIsDirty: false, saveUniquePriceId: false, customer: customer });
            this.closeWindow();
        }
        catch (error: any) {
            this.setState({ errorMessage: `Error while saving customer: ${error.message}`, inProgress: "" });
        }
    }

    // Associate the company with the customer in HubSpot
    linkCustomer = async () => {
        try {
            if (!this.state.associateCustomerNumber)
                throw new Error("Please select a customer before associating.");

            this.setState({ errorMessage: "", inProgress: "Associating customer..." });

            if (this.props.companyId)
                await Api.patch<object>(`/api/companies/${this.props.companyId}`, { "d365_account_number": this.state.associateCustomerNumber });

            this.setState({ inProgress: "" });
            this.closeWindow();
        }
        catch (error: any) {
            this.setState({ errorMessage: `Error while saving customer: ${error.message}`, inProgress: "" });
        }
    }

    deleteCustomer = async () => {
        try {
            this.setState({ errorMessage: "", inProgress: "Deleting customer..." });

            await Api.delete(`/api/customers/${this.state.customer.customerNumber}`);
            this.setState({ showDeleteDlg: false, customer: new Customer(), inProgress: "" });
            this.closeWindow();
        }
        catch (error: any) {
            this.setState({ errorMessage: `Error while deleting the customer: ${error.message}`, inProgress: "", showDeleteDlg: false });
            return;
        }
    }

    onDoCustomerSearch = async (value: string, exact?: boolean): Promise<ListItem[]> => {
        return exact
            ? [await Api.get<ListItem>(`/api/customers/${value}/list-item`)]
            : await Api.get<ListItem[]>(`/api/customers/list-items?search=${encodeURIComponent(value)}`);
    }

    onAssociateCustomerChange = (value: string) => {
        this.setState({ associateCustomerNumber: value });
    }

    onSetErrorMessage = (error: string) => {
        this.setState({ errorMessage: error });
    }

    private _territoryTimer: number | undefined = undefined;
    private updateTerritory = async () => {
        if (this._territoryTimer)
            window.clearTimeout(this._territoryTimer);
        this._territoryTimer = window.setTimeout(async () => {
            this._territoryTimer = undefined;
            let customer = new Customer(this.state.customer);
            let territory = await Api.get<Territory>(`/api/customers/territories?name=${encodeURIComponent(customer.name)}&segment=${customer.segment}&province=${customer.address.postal.stateOrProvince}&country=${customer.address.postal.countryCode}`);
            // Default to the AE as the sales person unless the CSM or the MCS have been selected
            if (!(customer.salespersonCode === territory.roles[OwnerRoles.CSM] || customer.salespersonCode === territory.roles[OwnerRoles.MCS]))
                customer.salespersonCode = territory.roles[OwnerRoles.AE];
            customer.territoryCode = territory.territoryCode;
            this.setState({ customer: customer, territory: territory, errorMessage: "" });
        }, 500);
    }

    onNameChange = async (value: string) => {
        let customer = new Customer(this.state.customer);
        customer.name = value;
        customer.address.companyName = value;
        this.setState({ customer: customer }, this.updateTerritory);
    }

    onSegmentChange = async (value: string) => {
        let customer = new Customer(this.state.customer);
        if (customer.inheritsPricing && !customer.parentCustomerNumber) {
            const oldSegment = new Segment(customer.segment);
            const newSegment = new Segment(value);
            if (oldSegment.defaultPriceGroupId !== newSegment.defaultPriceGroupId || oldSegment.defaultPriceId !== newSegment.defaultPriceId) {
                this.checkForCountryChange(customer.address, value);
                this.setState({ pendingSegment: value });
                return;
            }
        }
        customer.segment = value;
        this.checkForCountryChange(customer.address, value);
        this.setState({ customer: customer }, this.updateTerritory);
    }

    private tier2 = ['GBR', 'FIN', 'NLD', 'MCO', 'MLT', 'LUX', 'LIE', 'IMN', 'ISL', 'ITA', 'JEY', 'GIB', 'GRL', 'GRC', 'GGY', 'DNK', 'DEU', 'ESP', 'FRA', 'FRO', 'CHE', 'ALA', 'AND', 'BEL', 'AUT', 'SWE', 'SMR', 'SJM', 'PRT', 'NOR', 'VAT', 'UKR', 'TUR', 'SVK', 'SVN', 'ROU', 'RUS', 'SRB', 'POL', 'MNE', 'MKD', 'LVA', 'MDA', 'LTU', 'HUN', 'HRV', 'GEO', 'CZE', 'EST', 'BLR', 'BIH', 'BGR', 'ALB', 'ARM', 'AZE', 'PRI', 'IRL', 'AFG', 'BGD', 'BHR', 'BRN', 'BTN', 'CHN', 'CYP', 'HKG', 'IDN', 'JPN', 'KAZ', 'KGZ', 'KHM', 'KOR', 'LAO', 'MAC', 'MDV', 'MNG', 'MMR', 'MYS', 'NPL', 'PAK', 'PRK', 'SGP', 'THA', 'TJK', 'TKM', 'TLS', 'TWN', 'UZB', 'VNM', 'MHL', 'MNP', 'KIR', 'IOT', 'GUM', 'FSM', 'FJI', 'CCK', 'COK', 'CXR', 'ATF', 'ASM', 'TUV', 'TON', 'TKL', 'SLB', 'SGS', 'PYF', 'PLW', 'PNG', 'PCN', 'NRU', 'NIU', 'NCL', 'NFK', 'VUT', 'WLF', 'WSM'];
    private tier3 = ['ZAF', 'YEM', 'SYR', 'SAU', 'OMN', 'PSE', 'QAT', 'JOR', 'KWT', 'LBN', 'ISR', 'IRN', 'IRQ', 'ARE', 'AUS', 'NZL', 'ATG', 'ABW', 'AIA', 'BHS', 'BLM', 'BRB', 'FLK', 'DMA', 'DOM', 'CRI', 'CUB', 'CUW', 'CYM', 'GLP', 'GRD', 'HTI', 'JAM', 'LCA', 'KNA', 'MAF', 'MSR', 'MTQ', 'SXM', 'TCA', 'TTO', 'VCT', 'VGB', 'VIR'];
    private tier4 = ['IND', 'ATA', 'BLZ', 'BOL', 'BRA', 'FLK', 'ECU', 'ARG', 'CHL', 'COL', 'GTM', 'GUF', 'GUY', 'HND', 'MEX', 'NIC', 'PRY', 'PER', 'PAN', 'SLV', 'SUR', 'URY', 'VEN', 'LKA', 'PHL', 'TUN', 'EGY', 'DZA', 'ESH', 'MAR', 'LBY', 'TZA', 'UGA', 'ZMB', 'ZWE', 'NGA', 'NER', 'MUS', 'MWI', 'MYT', 'NAM', 'REU', 'SHN', 'SDN', 'SEN', 'RWA', 'SLE', 'SOM', 'SSD', 'STP', 'TCD', 'TGO', 'SYC', 'SWZ', 'BDI', 'AGO', 'BVT', 'BWA', 'CAF', 'BEN', 'BFA', 'COM', 'CPV', 'CIV', 'CMR', 'COD', 'COG', 'DJI', 'ETH', 'ERI', 'GAB', 'GMB', 'GNB', 'GNQ', 'GIN', 'GHA', 'HMD', 'KEN', 'LBR', 'LSO', 'MOZ', 'MRT', 'MLI', 'MDG'];
    private checkForCountryChange = (value: CustomerAddress, customerSegment: string) => {
        // If this is Higher Ed, determine the price code for the country
        const segment = new Segment(customerSegment);
        let customer = this.state.customer;
        if (!customer.customerNumber && segment.parentSegment?.code === Segment.higherEd.code) {
            if (this.tier2.indexOf(value.postal.countryCode) >= 0 && customer.priceId !== 136) {
                this.setState({ pendingPriceCode: "6-136-AX" });
            }
            else if (this.tier3.indexOf(value.postal.countryCode) >= 0 && customer.priceId !== 137) {
                this.setState({ pendingPriceCode: "6-137-AW" });
            }
            else if (this.tier4.indexOf(value.postal.countryCode) >= 0 && customer.priceId !== 138) {
                this.setState({ pendingPriceCode: "6-138-AV" });
            } else if ((value.postal.countryCode === 'USA' || value.postal.countryCode === 'CAN') && customer.priceId !== segment.defaultPriceId) {
                this.setState({ pendingPriceCode: segment.defaultPriceGroupId + "-" + segment.defaultPriceId + "-AD" });
            }
        }
    }

    onAddressChange = async (value: CustomerAddress) => {
        let customer = new Customer(this.state.customer);
        let updateTerritory = false;
        if (value.postal.stateOrProvince !== customer.address.postal.stateOrProvince)
            updateTerritory = true;
        if (value.postal.countryCode !== this.state.customer.address.postal.countryCode) {
            updateTerritory = true;
            this.checkForCountryChange(value, customer.segment);
        }
        customer.address = value;
        if (updateTerritory)
            this.setState({ customer: customer }, this.updateTerritory);
        else
            this.setState({ customer: customer });
    }

    onParentCustomerChange = async (value: string) => {
        let customer = new Customer(this.state.customer);
        if (customer.inheritsPricing)
            if (value) {
                // Inherits from parent
                const parentCustomer = await Api.getObject(Customer, `/api/customers/${value}`);
                if (parentCustomer && (customer.priceGroupId !== parentCustomer.priceGroupId || customer.priceId !== parentCustomer.priceId)) {
                    this.setState({ pendingParentCustomerNumber: value });
                    return;
                }
            }
            else {
                // Inherits from segment
                const segment = new Segment(customer.segment);
                if (segment && (customer.priceGroupId !== segment.defaultPriceGroupId || customer.priceId !== segment.defaultPriceId)) {
                    this.setState({ pendingParentCustomerNumber: value });
                    return;
                }
            }
        customer.parentCustomerNumber = value;
        this.setState({ customer: customer });
    }

    onInheritPricingChange = async (value: boolean) => {
        this.setState({ pendingInheritPricing: value });
    }

    onPriceCodeChange = async (value: string) => {
        this.setState({ pendingPriceCode: value });
    }

    private async getNewestPriceCode(priceGroupId: number, priceId: number): Promise<PriceGroupCode> {
        let pgc = await Api.getObject(PriceGroupCode, `/api/prices/pricegroupcodebyid?priceGroupId=${priceGroupId}&priceId=${priceId}`);
        if (!pgc) {
            pgc = new PriceGroupCode();
            pgc.priceGroupId = priceGroupId;
            pgc.priceId = priceId;
            pgc.version = "A";
        }
        return pgc;
    }

    private async getInheritedPriceCode(customerNumber?: string): Promise<PriceGroupCode|undefined> {
        if (customerNumber) {
            // Inherits from parent
            const parentCustomer = await Api.getObject(Customer, `/api/customers/${customerNumber}`);
            if (!parentCustomer)
                throw new Error(`Parent customer ${customerNumber} for customer ${this.state.customer.customerNumber} was not found.`);
            if (this.state.customer.priceGroupId !== parentCustomer.priceGroupId || this.state.customer.priceId !== parentCustomer.priceId)
                return await this.getNewestPriceCode(parentCustomer.priceGroupId, parentCustomer.priceId);
        }
        else {
            // Inherits from segment
            const segment = new Segment(this.state.customer.segment);
            if (!segment)
                throw new Error(`Segment ${this.state.customer.segment} for customer ${this.state.customer.customerNumber} was not found.`)
            if (this.state.customer.priceGroupId !== segment.defaultPriceGroupId || this.state.customer.priceId !== segment.defaultPriceId)
                return await this.getNewestPriceCode(segment.defaultPriceGroupId, segment.defaultPriceId);
        }

        // Price code is the same as current customer
        return undefined;
    }

    updatePricing = async () => {
        let customer = new Customer(this.state.customer);

        let pgc: PriceGroupCode|undefined = undefined;
        if (this.state.pendingParentCustomerNumber !== undefined) {
            if (customer.inheritsPricing)
                pgc = await this.getInheritedPriceCode(this.state.pendingParentCustomerNumber);
            customer.parentCustomerNumber = this.state.pendingParentCustomerNumber;

        } else if (this.state.pendingSegment !== undefined) {
            if (customer.inheritsPricing && !customer.parentCustomerNumber) {
                const oldSegment = new Segment(customer.segment);
                const newSegment = new Segment(this.state.pendingSegment);
                if (oldSegment.defaultPriceGroupId !== newSegment.defaultPriceGroupId || oldSegment.defaultPriceId !== newSegment.defaultPriceId)
                    pgc = await this.getNewestPriceCode(newSegment.defaultPriceGroupId, newSegment.defaultPriceId);
            }
            customer.segment = this.state.pendingSegment;

        } else if (this.state.pendingInheritPricing !== undefined) {
            if (this.state.pendingInheritPricing)
                pgc = await this.getInheritedPriceCode(customer.parentCustomerNumber);
            else {
                if (!customer.uniquePriceGroupId)
                    customer.uniquePriceGroupId = await Api.get(`/api/prices/group-id?customerNumber=${customer.customerNumber}`);
                if (!customer.uniquePriceGroupId)
                    throw new Error("UniquePriceGroupId failed to be created.");
                else
                    this.setState({ saveUniquePriceId: true });
                let priceIds = await Api.get<ListItem[]>(`/api/prices/grouppricelists/${customer.uniquePriceGroupId}`);

                pgc = await this.getNewestPriceCode(customer.uniquePriceGroupId, priceIds?.length > 0 && priceIds[0].value ? parseInt(priceIds[0].value) : new Segment(customer.segment).defaultPriceId);
            }
        }
        if (this.state.pendingPriceCode !== undefined) {
            pgc = PriceGroupCode.getNewPriceGroupCode(this.state.pendingPriceCode);
            if (customer.uniquePriceGroupId)
                this.setState({ saveUniquePriceId: true });
        }

        // pgc will be undefined if pricing hasn't changed
        if (pgc) {
            const shouldUpdateTerritory = this.state.pendingSegment !== undefined;
            if (customer.priceGroupId !== pgc.priceGroupId) {
                // Reload the price list for this price group
                let list = await Api.get<ListItem[]>(`/api/prices/grouppricecodes/${pgc.priceGroupId}/list-items`);
                this.setState({ priceCodes: list });
            }
            customer.priceGroupId = pgc.priceGroupId;
            customer.priceId = pgc.priceId;
            this.setState({
                customer: customer,
                priceCode: pgc.priceCode,
                pendingInheritPricing: undefined,
                pendingPriceCode: undefined,
                pendingParentCustomerNumber: undefined,
                pendingSegment: undefined
            }, () => {
                if (shouldUpdateTerritory)
                    this.updateTerritory()
            });
        }
    }

    onBillingCustomerChange = (value: string) => {
        let customer = new Customer(this.state.customer);
        customer.billingCustomerNumber = value;
        this.setState({ customer: customer });
    }

    onBillingTypeChange = (value: string) => {
        this.setState({ customer: new Customer({ ...this.state.customer, billingType: parseInt(value) }) });
    }

    onCreditHoldChange = (value: boolean) => {
        let customer = new Customer(this.state.customer);
        customer.creditOnHold = value;
        this.setState({ customer: customer });
    }

    onPaymentTermsChange = (value: string) => {
        let customer = new Customer(this.state.customer);
        customer.paymentTerms = value;
        this.setState({ customer: customer });
    }

    onCommentsChange = (value: string) => {
        let customer = new Customer(this.state.customer);
        customer.comments = value;
        this.setState({ customer: customer });
    }

    onAutomatedFulfillmentChange = (value: boolean) => {
        let customer = new Customer(this.state.customer);
        customer.useAutomatedFulfillment = value;
        this.setState({ customer: customer });
    }

    onShowInCartChange = (value: boolean) => {
        let customer = new Customer(this.state.customer);
        customer.hideInCart = !value;
        this.setState({ customer: customer });
    }

    onTaxExemptionNumberChange = (value: string) => {
        let taxCertificate = new TaxCertificate(this.state.taxCertificate);
        taxCertificate.exemptionNumber = value;
        if (!taxCertificate.effectiveDate)
            taxCertificate.effectiveDate = new Date(Date.now());
        if (!taxCertificate.expirationDate) {
            let date: Date = new Date(Date.now());
            date.setFullYear(date.getFullYear() + 10);
            taxCertificate.expirationDate = date;
        }
        this.setState({ taxCertificate: taxCertificate, taxIsDirty: true });
    }

    onTaxExemptionFileChange = async (value?: File) => {
        if (value) {
            let taxCertificate = new TaxCertificate(this.state.taxCertificate);
            taxCertificate.document = new Document();
            taxCertificate.document.fileContent = value;
            this.setState({ taxCertificate: taxCertificate, taxIsDirty: true });
        }
    }

    onTaxExemptionEffectiveDateChange = (value?: Date) => {
        let taxCertificate = new TaxCertificate(this.state.taxCertificate);
        taxCertificate.effectiveDate = value;
        this.setState({ taxCertificate: taxCertificate, taxIsDirty: true });
    }

    onTaxExemptionExpirationDateChange = (value?: Date) => {
        let taxCertificate = new TaxCertificate(this.state.taxCertificate);
        taxCertificate.expirationDate = value;
        this.setState({ taxCertificate: taxCertificate, taxIsDirty: true });
    }

    onSalespersonChange = (value: string) => {
        let customer = new Customer(this.state.customer);
        customer.salespersonCode = value;
        this.setState({ customer: customer });
    }

    closeWindow = () => {
        window.parent.postMessage(JSON.stringify({ action: "CANCEL" }), "*");
    }

    render() {
        if (!this.state.initialized)
            return <div className="page CustomerPage loading"><div className="spinner"></div></div>;

        if (this.state.showAssociateDlg)
            return <div className="page CustomerPage">
                <header>
                    <p>Select the existing customer to associate with company <span className="bold">{this.state.company?.name}</span> located
                        in {this.state.company?.city}{this.state.company?.state ? ", " + this.state.company?.state : ""}{this.state.company?.country !== "USA" ? " " + this.state.company?.country : ""}.
                        If a matching customer does not exist, click <span className="italic">Associate with new customer</span> below to create and associate a new one.
                        </p>
                    {this.state.errorMessage &&
                        <p className="error">{this.state.errorMessage}</p>}
                </header>
                <main>
                   <section className="common">
                        <LabelCtl label="Customer">
                            <LookupCtl className="associate" placeholder="Search customers" disabled={false} value={this.state.associateCustomerNumber} onSearch={this.onDoCustomerSearch}
                                onChange={this.onAssociateCustomerChange} alwaysOpen={true} initialSearchValue={this.state.company?.name} />
                       </LabelCtl>
                   </section>
                </main>

                <footer>
                    <button className="tertiary" onClick={() => this.initCustomer()}>Associate with new customer</button>
                    <button className="secondary" onClick={() => this.closeWindow()}>Cancel</button>
                    <button className="primary" onClick={() => this.linkCustomer()}>Associate</button>
                </footer>

                {this.state.inProgress &&
                    <div className="saving"><div><div>{this.state.inProgress}</div><div className="spinner"></div></div></div>}
            </div>;

        let { customer, territory } = this.state;
        return <div className="page CustomerPage">
           <header>
               <div className="title">
                    <h3>{!customer.customerNumber ? " -- New Customer -- " : `${customer.customerNumber} - ${customer.name}`}</h3>
                    <h6><OwnerCtl value={customer.salespersonCode} territory={territory} employees={this.state.employees} onChange={this.onSalespersonChange} /></h6>
               </div>
               <div className="actions">
                </div>
               {this.state.errorMessage &&
                    <p className="error">{this.state.errorMessage}</p>}
               {customer.creditOnHold &&
                    <p className="warning">This customer is currently on credit hold. Please check with accounting before submitting orders.</p>}
               {customer.comments &&
                    <p className="info">{customer.comments}</p>}
           </header>

           <main>
               <section className="left tax">
                   <LabelCtl label="Name">
                       <TextCtl value={customer.name} disabled={false} onChange={this.onNameChange}/>
                   </LabelCtl>
                   <LabelCtl label="Segment">
                       <SelectCtl value={customer.segment} disabled={false} items={Segment.items} hideItemValue={true} onChange={this.onSegmentChange}/>
                    </LabelCtl>
                    <div className="label-group">
                        <LabelCtl label="Primary Address" useDiv={true}>
                            <CustomerAddressCtl noSave={true} customerNumber={customer.customerNumber} address={customer.address}
                                                onChange={this.onAddressChange} onSetErrorMessage={this.onSetErrorMessage} />
                        </LabelCtl>
                        <button disabled={!customer.customerNumber} className="link link-right"
                            onClick={() => this.setState({ showAddressDlg: true })}
                            title={!this.props.customerNumber ? "Save the customer to enable other addresses" : undefined}>Other Addresses</button>
                    </div>
                   <h3>Tax Exemption</h3>
                   <LabelCtl label="ID/Number">
                       <TextCtl value={this.state.taxCertificate?.exemptionNumber} onChange={this.onTaxExemptionNumberChange} />
                   </LabelCtl>
                    <LabelCtl label="Certificate">
                        <FileCtl placeholder="Select a file..." accept="application/pdf" value={this.state.taxCertificate?.document?.fileContent} onChange={this.onTaxExemptionFileChange} />
                   </LabelCtl>

                   <div className="label-group">
                       <LabelCtl label="Effective Date">
                            <DateCtl value={this.state.taxCertificate?.effectiveDate} onChange={this.onTaxExemptionEffectiveDateChange} />
                       </LabelCtl>
                       <LabelCtl label="Expiration Date">
                            <DateCtl value={this.state.taxCertificate?.expirationDate} onChange={this.onTaxExemptionExpirationDateChange} />
                       </LabelCtl>
                   </div>
               </section>

               <section className="right alert">
                   <LabelCtl label="Parent Customer">
                       <LookupCtl placeholder="Search customers" disabled={false} value={customer.parentCustomerNumber} onSearch={this.onDoCustomerSearch}
                                  onChange={this.onParentCustomerChange}/>
                   </LabelCtl>
                   <h3>Billing and Fulfillment</h3>
                   <LabelCtl label="Billing Customer">
                       <LookupCtl placeholder="Search customers" value={customer.billingCustomerNumber} onSearch={this.onDoCustomerSearch}
                                  onChange={this.onBillingCustomerChange}/>
                   </LabelCtl>
                   <LabelCtl label="Billing Type">
                       <SelectCtl value={customer.billingType.toString()} items={this._billingTypes} hideItemValue={true} onChange={this.onBillingTypeChange}/>
                   </LabelCtl>
                   <div className="label-group">
                       <LabelCtl label="Price Code">
                           <SelectCtl value={this.state.priceCode} items={this.state.priceCodes} hideItemValue={false} onChange={this.onPriceCodeChange} disabled={customer.inheritsPricing}/>
                        </LabelCtl>
                        <CheckCtl className="check-right" label={customer.parentCustomerNumber ? "Inherit from Parent Customer" : "Inherit from Segment"} value={customer.inheritsPricing} onChange={this.onInheritPricingChange} />
                   </div>
                   <div className="label-group">
                       <LabelCtl label="Payment Terms">
                           <SelectCtl value={customer.paymentTerms} items={this._paymentTerms} hideItemValue={true} onChange={this.onPaymentTermsChange} disabled={customer.creditOnHold}/>
                       </LabelCtl>
                       <CheckCtl className="check-right" label="Credit Hold" value={customer.creditOnHold} onChange={this.onCreditHoldChange}/>
                   </div>
                   <CheckCtl className="vert-break" label="Use automated order fulfillment" value={customer.useAutomatedFulfillment} onChange={this.onAutomatedFulfillmentChange}/>
                   <CheckCtl label="Show customer in shopping cart institution list" value={customer.showInCart} onChange={this.onShowInCartChange}/>
                    <LabelCtl label="Alert">
                       <TextCtl type="textarea" value={customer.comments} onChange={this.onCommentsChange} />
                   </LabelCtl>
               </section>
           </main>

            <footer>
                <button className="tertiary" disabled={!this.props.customerNumber || !!this.state.inProgress || isAccountingPersonnel()}
                    onClick={() => this.setState({ showDeleteDlg: true })}>Delete</button>
                <button className="secondary" onClick={() => this.closeWindow()}>{this.props.companyId ? "Cancel" : "Close"}</button>
                <button className="primary" disabled={!!this.state.inProgress}
                    onClick={() => this.saveCustomer()}>Save{this.props.companyId ? " and Associate" : ""}</button>
           </footer>

            {this.state.showDeleteDlg &&
                <ModalCtl headerText="Delete this customer?" description="Please confirm you wish to delete this customer."
                    submitButton="Delete"
                    saveInProgress={!!this.state.inProgress}
                onSubmit={() => this.deleteCustomer()} onCancel={() => this.setState({ showDeleteDlg: false })} />}

            <Dialog title="Confirm Pricing Change" isOpen={
                this.state.pendingInheritPricing !== undefined ||
                this.state.pendingPriceCode !== undefined ||
                this.state.pendingParentCustomerNumber !== undefined ||
                this.state.pendingSegment !== undefined} className="ConfirmPricingDlg">
                <p>This change will cause the price code for this customer to be updated. If the price code is used by students, you will need to ensure it is communicated to the customer.</p>
                <p>Please confirm you want to update the pricing for this customer.</p>
                <footer>
                    <button className="primary" onClick={this.updatePricing}>Update Pricing</button>
                    <button className="secondary" onClick={() => this.setState({ pendingInheritPricing: undefined, pendingPriceCode: undefined, pendingParentCustomerNumber: undefined, pendingSegment: undefined })}>Cancel</button>
                </footer>
            </Dialog>

            <CustomerAddressDlg hsCompanyId={this.props.companyId ? parseInt(this.props.companyId) : undefined}
                customerNumber={this.state.customer.customerNumber}
                defaultAddress={this.state.showAddressDlg ? new CustomerAddress(this.state.customer.address) : undefined}
                isOpen={this.state.showAddressDlg} onClose={() => this.setState({ showAddressDlg: false })} />

            {this.state.inProgress &&
                <div className="saving"><div><div>{this.state.inProgress}</div><div className="spinner"></div></div></div>}
        </div>;
    }
}
